// src/pages/student/EventsListing.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
} from "@mui/material";
import {
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { getAllEvents, registerForEvent } from "../../services/eventService";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../AdminLayout";

export default function EventsListing() {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        studentId: "",
        email: "",
        course: "",
        year: "",
        mobileNo: "",
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        if (userProfile) {
            setFormData((prev) => ({
                ...prev,
                name: `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim(),
                email: userProfile.email || "",
                studentId: userProfile.studentId || userProfile.uid || "",
            }));
        }
    }, [userProfile]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const data = await getAllEvents();
            // don't filter by date; hide only Closed from listing if you want, but we show all
            setEvents(data);
        } catch {
            setError("Failed to fetch events");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleRegister = async () => {
        try {
            if (
                !formData.name ||
                !formData.studentId ||
                !formData.email ||
                !formData.course ||
                !formData.year ||
                !formData.mobileNo
            ) {
                setError("Please fill in all required fields");
                return;
            }

            const result = await registerForEvent(selectedEvent.id, {
                ...formData,
                userId: user?.uid || userProfile?.uid || ""
            });
            if (result.success) {
                setSuccess("Successfully registered for the event!");
                setRegisterDialogOpen(false);
                fetchEvents();
            } else {
                setError(result.message);
            }
        } catch {
            setError("Failed to register. Please try again.");
        }
    };

    const isRegistered = (event) => {
        return (event.registrations || []).some(
            (r) => r.studentId === userProfile?.studentId || r.email === userProfile?.email
        );
    };

    const statusChip = (event) => {
        const status = event.eventStatus || "Upcoming";
        const map = {
            Upcoming: { color: "success", label: "Open for Registration" },
            Ongoing: { color: "info", label: "Ongoing" },
            Completed: { color: "primary", label: "Completed" },
            Closed: { color: "default", label: "Closed" },
        };
        const conf = map[status];
        return <Chip label={conf.label} color={conf.color} size="small" />;
    };

    return (
        <AdminLayout title="Events">
            <Box>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "#003366", mb: 3 }}
                >
                    Events & Sessions
                </Typography>

                {error && (
                    <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3} sx={{ mb: 2 }}>
                    {loading ? (
                        <Grid item xs={12}>
                            <Typography align="center">Loading events...</Typography>
                        </Grid>
                    ) : events.length === 0 ? (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography align="center" color="textSecondary">
                                        No events available at the moment.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ) : (
                        events
                            .filter(e => (e.type || "internal") === "internal")
                            .map((event) => {
                                const registered = isRegistered(event);
                                const isFull =
                                    event.maxCapacity &&
                                    (event.registrations?.length || 0) >= event.maxCapacity;
                                const isClosed = event.eventStatus === "Closed";

                                return (
                                    <Grid item xs={12} sm={6} lg={3} key={event.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderColor: registered ? "#C1272D" : "divider",
                                                borderWidth: registered ? 1.5 : 1,
                                                borderRadius: 2,
                                                "&:hover": {
                                                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.08)",
                                                    transform: "translateY(-2px)",
                                                },
                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "flex-start",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 700, color: "#003366", pr: 1, lineHeight: 1.3 }}
                                                    >
                                                        {event.title}
                                                    </Typography>
                                                    {statusChip(event)}
                                                </Box>

                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        mb: 1.5,
                                                        minHeight: 34,
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    {event.description}
                                                </Typography>

                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <CalendarIcon sx={{ fontSize: 16, color: "#003366" }} />
                                                        <Typography variant="caption">
                                                            {event.eventDate
                                                                ? new Date(event.eventDate).toLocaleDateString()
                                                                : "Date TBA"}
                                                            {event.eventTime && ` at ${event.eventTime}`}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LocationIcon sx={{ fontSize: 16, color: "#003366" }} />
                                                        <Typography variant="caption">
                                                            {event.venue || "Venue TBA"}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", mt: 0.5 }}>
                                                        <Chip label={(event.type || "internal").toUpperCase()} size="small" variant="outlined" />
                                                    </Box>
                                                </Box>

                                                {registered && (
                                                    <Chip
                                                        label="✓ Registered"
                                                        color="success"
                                                        size="small"
                                                        sx={{ mt: 1.25 }}
                                                    />
                                                )}
                                            </CardContent>

                                            <Box sx={{ p: 1.5, borderTop: "1px solid #e0e0e0" }}>
                                                {registered ? (
                                                    <Button fullWidth disabled>
                                                        Already Registered
                                                    </Button>
                                                ) : isClosed ? (
                                                    <Button fullWidth disabled>
                                                        Registrations Closed
                                                    </Button>
                                                ) : isFull ? (
                                                    <Button fullWidth disabled>
                                                        Event Full
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => {
                                                            if ((event.type || "internal") === "external") {
                                                                navigate(`/student/events-v2/${event.id}`);
                                                            } else {
                                                                setSelectedEvent(event);
                                                                setRegisterDialogOpen(true);
                                                            }
                                                        }}
                                                        sx={{
                                                            backgroundColor: "#C1272D",
                                                            "&:hover": { backgroundColor: "#a01f25" },
                                                        }}
                                                    >
                                                        {(event.type || "internal") === "external" ? "View & Attempt Quiz" : "Register Now"}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })
                    )}
                </Grid>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#003366", mb: 2 }}>
                    External Events
                </Typography>
                <Grid container spacing={3}>
                    {loading ? (
                        <Grid item xs={12}>
                            <Typography align="center">Loading events...</Typography>
                        </Grid>
                    ) : events.length === 0 ? (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography align="center" color="textSecondary">
                                        No events available at the moment.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ) : (
                        events
                            .filter(e => (e.type || "internal") === "external")
                            .map((event) => {
                                const registered = isRegistered(event);
                                const isFull =
                                    event.maxCapacity &&
                                    (event.registrations?.length || 0) >= event.maxCapacity;
                                const isClosed = event.eventStatus === "Closed";

                                return (
                                    <Grid item xs={12} sm={6} lg={3} key={event.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderColor: registered ? "#C1272D" : "divider",
                                                borderWidth: registered ? 1.5 : 1,
                                                borderRadius: 2,
                                                "&:hover": {
                                                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.08)",
                                                    transform: "translateY(-2px)",
                                                },
                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "flex-start",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 700, color: "#003366", pr: 1, lineHeight: 1.3 }}
                                                    >
                                                        {event.title}
                                                    </Typography>
                                                    {statusChip(event)}
                                                </Box>

                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        mb: 1.5,
                                                        minHeight: 34,
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    {event.description}
                                                </Typography>

                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <CalendarIcon sx={{ fontSize: 16, color: "#003366" }} />
                                                        <Typography variant="caption">
                                                            {event.eventDate
                                                                ? new Date(event.eventDate).toLocaleDateString()
                                                                : "Date TBA"}
                                                            {event.eventTime && ` at ${event.eventTime}`}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LocationIcon sx={{ fontSize: 16, color: "#003366" }} />
                                                        <Typography variant="caption">
                                                            {event.venue || "Venue TBA"}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", mt: 0.5 }}>
                                                        <Chip label={(event.type || "internal").toUpperCase()} size="small" variant="outlined" />
                                                    </Box>
                                                </Box>

                                                {registered && (
                                                    <Chip
                                                        label="✓ Registered"
                                                        color="success"
                                                        size="small"
                                                        sx={{ mt: 1.25 }}
                                                    />
                                                )}
                                            </CardContent>

                                            <Box sx={{ p: 1.5, borderTop: "1px solid #e0e0e0" }}>
                                                {registered ? (
                                                    <Button fullWidth disabled>
                                                        Already Registered
                                                    </Button>
                                                ) : isClosed ? (
                                                    <Button fullWidth disabled>
                                                        Registrations Closed
                                                    </Button>
                                                ) : isFull ? (
                                                    <Button fullWidth disabled>
                                                        Event Full
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => {
                                                            navigate(`/student/events-v2/${event.id}`);
                                                        }}
                                                        sx={{
                                                            backgroundColor: "#C1272D",
                                                            "&:hover": { backgroundColor: "#a01f25" },
                                                        }}
                                                    >
                                                        View & Attempt Quiz
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })
                    )}
                </Grid>

                {/* Registration Dialog */}
                <Dialog
                    open={registerDialogOpen}
                    onClose={() => setRegisterDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ color: "#003366", fontWeight: 600 }}>
                        Register for {selectedEvent?.title}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                            <TextField
                                fullWidth
                                required
                                label="Full Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Student / Employee ID"
                                name="studentId"
                                value={formData.studentId}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                required
                                type="email"
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Course / Department"
                                name="course"
                                value={formData.course}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Year / Designation"
                                name="year"
                                value={formData.year}
                                onChange={handleChange}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Mobile Number"
                                name="mobileNo"
                                value={formData.mobileNo}
                                onChange={handleChange}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRegisterDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleRegister}
                            variant="contained"
                            sx={{
                                backgroundColor: "#C1272D",
                                "&:hover": { backgroundColor: "#a01f25" },
                            }}
                        >
                            Register
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
